import { useEffect, useState } from "react";
import { Auth as SupabaseAuth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import type { AuthError } from "@supabase/supabase-js";

const Auth = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          // Check if user is admin
          const { data: roles } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', session.user.id)
            .single();

          if (roles?.role === 'admin') {
            navigate("/admin");
          } else {
            navigate("/");
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session) {
        // Check if user is admin after sign in
        const { data: roles } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .single();

        if (roles?.role === 'admin') {
          navigate("/admin");
        } else {
          navigate("/");
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const getErrorMessage = (error: AuthError) => {
    switch (error.message) {
      case "Invalid login credentials":
        return "بيانات تسجيل الدخول غير صحيحة";
      case "Email not confirmed":
        return "الرجاء تأكيد بريدك الإلكتروني";
      case "User already registered":
        return "البريد الإلكتروني مسجل مسبقاً";
      case "Password should be at least 6 characters":
        return "كلمة المرور يجب أن تكون 6 أحرف على الأقل";
      default:
        return error.message;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-3xl font-bold text-primary">
            مرحباً بك
          </CardTitle>
          <p className="text-muted-foreground">
            قم بتسجيل الدخول للوصول إلى حسابك
          </p>
        </CardHeader>
        <CardContent>
          {errorMessage && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}
          <SupabaseAuth
            supabaseClient={supabase}
            appearance={{
              theme: ThemeSupa,
              variables: {
                default: {
                  colors: {
                    brand: 'hsl(var(--primary))',
                    brandAccent: 'hsl(var(--primary))',
                    brandButtonText: 'white',
                  },
                  radii: {
                    borderRadiusButton: '0.5rem',
                    buttonBorderRadius: '0.5rem',
                    inputBorderRadius: '0.5rem',
                  },
                },
              },
              className: {
                container: 'w-full',
                button: 'w-full font-medium rounded-lg',
                input: 'rounded-lg',
              },
            }}
            providers={["google"]}
            localization={{
              variables: {
                sign_in: {
                  email_label: "البريد الإلكتروني",
                  password_label: "كلمة المرور",
                  button_label: "تسجيل الدخول",
                  loading_button_label: "جاري تسجيل الدخول ...",
                  social_provider_text: "تسجيل الدخول باستخدام {{provider}}",
                  link_text: "لديك حساب بالفعل؟ قم بتسجيل الدخول",
                },
                sign_up: {
                  email_label: "البريد الإلكتروني",
                  password_label: "كلمة المرور",
                  button_label: "إنشاء حساب",
                  loading_button_label: "جاري إنشاء الحساب ...",
                  social_provider_text: "تسجيل الدخول باستخدام {{provider}}",
                  link_text: "ليس لديك حساب؟ قم بإنشاء حساب جديد",
                },
                forgotten_password: {
                  email_label: "البريد الإلكتروني",
                  password_label: "كلمة المرور",
                  button_label: "إرسال رابط إعادة تعيين كلمة المرور",
                  loading_button_label: "جاري إرسال الرابط ...",
                  link_text: "نسيت كلمة المرور؟",
                },
              },
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;